import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { RouterBreadcrumbs } from 'src/router';
import { useStoreUnitActions } from 'src/redux/helpers/createStoreUnit';
import { ServerRecord } from './data/inventoryData';
import { groupBySite, hasValidCoords, SiteGroup } from './utils/siteUtils';
import InfraKpis from './components/InfraMapKpis';
import InfraFilters, { FilterState } from './components/InfraMapFilters';
import InfraLeafletMap from './components/InfraMapLeafletMap';
import SiteDrawer from './components/InfraMapSiteDrawer';
import infraMapStore, { useInfraMapList } from './redux';

const INITIAL_FILTERS: FilterState = {
  search: '',
  type: '',
  gpu: '',
  useCase: '',
};

// mapMatchFn: text search + dropdowns — used to highlight pins on the map
function buildMatchFn(filters: FilterState) {
  return (x: ServerRecord): boolean => {
    const q = filters.search.trim().toLowerCase();
    const isPhysical = x.serverType.toLowerCase().includes('physical');
    const hasGpu = Number(x.GPUCount) > 0;
    const textOk = !q || `${x.server} ${x.site}`.toLowerCase().includes(q);
    const typeOk =
      !filters.type ||
      (filters.type === 'physical' && isPhysical) ||
      (filters.type === 'vm' && !isPhysical);
    const gpuOk =
      !filters.gpu ||
      (filters.gpu === 'gpu' && hasGpu) ||
      (filters.gpu === 'none' && !hasGpu);
    const useOk =
      !filters.useCase ||
      x.CurrUseCases.toLowerCase().includes(filters.useCase);
    return textOk && typeOk && gpuOk && useOk;
  };
}

// drawerMatchFn: dropdowns only — text search only finds sites, not filters the drawer
function buildDrawerMatchFn(filters: FilterState) {
  return (x: ServerRecord): boolean => {
    const isPhysical = x.serverType.toLowerCase().includes('physical');
    const hasGpu = Number(x.GPUCount) > 0;
    const typeOk =
      !filters.type ||
      (filters.type === 'physical' && isPhysical) ||
      (filters.type === 'vm' && !isPhysical);
    const gpuOk =
      !filters.gpu ||
      (filters.gpu === 'gpu' && hasGpu) ||
      (filters.gpu === 'none' && !hasGpu);
    const useOk =
      !filters.useCase ||
      x.CurrUseCases.toLowerCase().includes(filters.useCase);
    return typeOk && gpuOk && useOk;
  };
}

export default function InfraMap() {
  const [data, loading] = useInfraMapList();
  const actions = useStoreUnitActions(infraMapStore);

  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);
  const [selectedSite, setSelectedSite] = useState<SiteGroup | null>(null);

  useEffect(() => {
    actions.list();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const useCases = useMemo(
    () =>
      Array.from(
        new Set(
          data.flatMap((x) =>
            String(x.CurrUseCases || '')
              .split(',')
              .map((v) => v.trim())
              .filter((v) => v && v !== 'NA')
          )
        )
      ).sort(),
    [data]
  );

  const sites = useMemo(() => groupBySite(data), [data]);
  const matchFn = useMemo(() => buildMatchFn(filters), [filters]);
  const drawerMatchFn = useMemo(() => buildDrawerMatchFn(filters), [filters]);

  // Always derived — no stale state possible
  const visibleServers = useMemo(
    () => (selectedSite ? selectedSite.servers.filter(drawerMatchFn) : []),
    [selectedSite, drawerMatchFn]
  );

  const visibleSiteNames = useMemo(() => {
    const names = new Set<string>();
    Object.entries(sites).forEach(([name, site]) => {
      if (site.servers.some(matchFn)) names.add(name);
    });
    return names;
  }, [sites, matchFn]);

  // Only count servers whose site actually renders a pin on the map —
  // sites without a usable Latitude/Longitude are dropped from the map,
  // so they shouldn't be included in the "servers shown" count either.
  const totalVisible = useMemo(
    () =>
      Object.values(sites).reduce((sum, site) => {
        if (!hasValidCoords(site.Latitude, site.Longitude)) return sum;
        return sum + site.servers.filter(matchFn).length;
      }, 0),
    [sites, matchFn]
  );

  const handleSiteClick = useCallback(
    (site: SiteGroup) => {
      setSelectedSite(site);
    },
    []
  );

  if (loading !== false && data.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loading === false && data.length === 0) {
    return (
      <>
        <RouterBreadcrumbs />
        <Alert severity="warning" sx={{ mt: 2 }}>
          No infrastructure data available.
        </Alert>
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <RouterBreadcrumbs />

      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="h6" fontWeight={700}>
            Infrastructure Map
          </Typography>
          <Chip
            label={`${totalVisible} servers shown`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Box sx={{ ml: 'auto' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={
                loading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <RefreshIcon fontSize="small" />
                )
              }
              disabled={!!loading}
              onClick={() => actions.refresh()}
            >
              Refresh
            </Button>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary">
          North America operations — click a site pin to view server details
        </Typography>
      </Box>

      <InfraKpis data={data} sites={sites} />

      <InfraFilters
        filters={filters}
        useCases={useCases}
        onChange={setFilters}
        onClear={() => setFilters(INITIAL_FILTERS)}
      />

      <Box sx={{ flex: 1, minHeight: 400, borderRadius: 2, overflow: 'hidden' }}>
        <InfraLeafletMap
          sites={sites}
          visibleSiteNames={visibleSiteNames}
          matchFn={matchFn}
          onSiteClick={handleSiteClick}
        />
      </Box>

      <SiteDrawer
        site={selectedSite}
        visibleServers={visibleServers}
        onClose={() => setSelectedSite(null)}
      />
    </Box>
  );
}
